import type { TrackingClient } from '../types';
import { bindUnloadPageLeave } from '../core/bindUnloadPageLeave';
import {
  isLikelyNextAppRouter,
  resolveNextRouteName,
  resolveNextRouteNameWhenReady,
} from '../core/resolveNextRouteName';

type HistoryMethod = 'pushState' | 'replaceState';

/**
 * 监听 History API / hashchange / popstate / 页面隐藏与关闭，
 * 在 SPA 路由变化或离开时上报 page_leave +（路由变化时）page_view。
 * 返回卸载函数。
 *
 * route_name：优先识别 Next.js 动态路由模板（Pages: __NEXT_DATA__.page；
 * App Router: FlightRouterState），否则回退 pathname。
 */
export function bindSpaPageTracking(client: TrackingClient): () => void {
  if (typeof window === 'undefined' || typeof history === 'undefined') {
    return () => undefined;
  }

  let pageId = window.location.pathname || '/';
  let routeName = pageId;
  let enterAt = Date.now();
  let disposed = false;
  let viewSeq = 0;
  /** 是否已成功上报过当前页 page_view（首屏等待路由树期间为 false） */
  let hasActiveView = false;

  const unloadLeave = bindUnloadPageLeave(
    client,
    () =>
      hasActiveView
        ? { pageId, enterAt, routeName }
        : { pageId: '', enterAt, routeName },
    () => {
      // 从隐藏恢复：重新计时，不重复打 page_view，避免 PV 虚高
      enterAt = Date.now();
    }
  );

  const reportLeave = (leaveType: string) => {
    if (!hasActiveView) return;
    hasActiveView = false;
    client.pageLeave({
      page_id: pageId,
      duration_ms: Math.max(0, Date.now() - enterAt),
      leave_type: leaveType,
      route_name: routeName,
    });
  };

  const reportViewSync = (resolvedRouteName: string) => {
    pageId = window.location.pathname || '/';
    routeName = resolvedRouteName || pageId;
    enterAt = Date.now();
    hasActiveView = true;
    unloadLeave.reset();
    client.pageView({
      page_id: pageId,
      page_name: document.title || undefined,
      route_name: routeName,
      page_url: window.location.href,
      referrer_url: document.referrer || undefined,
    });
  };

  const reportView = () => {
    const currentPath = window.location.pathname || '/';
    // 路由切换后 Next 通常已写入 history.state，可同步解析
    const syncName = resolveNextRouteName(currentPath);
    if (syncName !== currentPath || !isLikelyNextAppRouter()) {
      reportViewSync(syncName);
      return;
    }

    // 首屏 App Router：等 hydration 写入路由树后再上报，避免 route_name 变成具体 id
    const seq = ++viewSeq;
    void resolveNextRouteNameWhenReady(currentPath).then((name) => {
      if (disposed || seq !== viewSeq) return;
      // 等待期间若已发生导航，以当前 path 为准
      const pathNow = window.location.pathname || '/';
      reportViewSync(pathNow === currentPath ? name : resolveNextRouteName(pathNow));
    });
  };

  const onNavigate = (leaveType: string) => {
    const nextPageId = window.location.pathname || '/';
    if (nextPageId === pageId) return;
    viewSeq += 1; // 作废进行中的首屏等待
    reportLeave(leaveType);
    reportView();
  };

  const wrapHistory = (method: HistoryMethod) => {
    const original = history[method];
    history[method] = function patched(
      this: History,
      ...args: Parameters<History[HistoryMethod]>
    ) {
      const result = original.apply(this, args);
      onNavigate('route_change');
      return result;
    } as History[HistoryMethod];
    return () => {
      history[method] = original;
    };
  };

  const unPatchPush = wrapHistory('pushState');
  const unPatchReplace = wrapHistory('replaceState');

  const onPopState = () => onNavigate('route_change');
  const onHashChange = () => onNavigate('route_change');
  window.addEventListener('popstate', onPopState);
  window.addEventListener('hashchange', onHashChange);

  // 首屏
  reportView();

  return () => {
    disposed = true;
    viewSeq += 1;
    unloadLeave.unbind();
    unPatchPush();
    unPatchReplace();
    window.removeEventListener('popstate', onPopState);
    window.removeEventListener('hashchange', onHashChange);
  };
}
